import { immutableSet } from '@icp/utils';
import { getTFunc } from '@icp/i18n';

export const IF_CONDITIONS = [
  'contains',
  'doesnot_contain',
  'starts_with',
  'doesnot_start_with',
  'ends_with',
  'doesnot_end_with',
  'equals',
  'does_not_equal',
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
  'is_true',
  'is_not_true',
  'is_present',
  'is_not_present',
];

export const spacerSize = 64;
export const handleSize = 10;

export function conditionNoNeedValue(condition) {
  return ['is_true', 'is_not_true', 'is_present', 'is_not_present'].includes(condition);
}

export function makeNewNode(type, workflowTypeNodeMap) {
  const common = {
    nodeTypeToken: type,
    name: workflowTypeNodeMap[type].name,
  };

  if (type === 'condition') {
    return {
      ...common,
      branches: [makeNewConditionBranch(), makeNewConditionBranch()],
    };
  }

  if (type === 'approval') {
    return {
      ...common,
      branches: [
        {
          result: 'APPROVAL',
          subFlow: [],
        },
        {
          result: 'DISAPPROVAL',
          subFlow: [],
        },
      ],
    };
  }

  return common;
}

export function makeNewConditionBranch() {
  return {
    subFlow: [],
  };
}

function forEachValue(value, cb) {
  const dfs = (nodeList, keyPath) => {
    if (!(Array.isArray(nodeList) && nodeList.length)) {
      return;
    }

    nodeList.forEach((node, index) => {
      const nodeKeyPath = keyPath.concat(index);
      cb(node, nodeKeyPath);
      dfs(node.branches, nodeKeyPath.concat('branches'));
      dfs(node.subFlow, nodeKeyPath.concat('subFlow'));
      dfs(node.conditions, nodeKeyPath.concat('conditions'));
    });
  };

  dfs(value.nodeList, ['nodeList']);
}

function findMaxId(value) {
  let maxId = 0;
  forEachValue(value, (node) => {
    if (typeof node.id === 'number' && node.id > maxId) {
      maxId = node.id;
    }
  });
  return maxId;
}

export function generateNextId(value) {
  return findMaxId(value) + 1;
}

export function checkValueId(value) {
  let newValue = value;
  forEachValue(value, (node, keyPath) => {
    if (!node.id || typeof node.id !== 'number') {
      newValue = immutableSet(newValue, keyPath.concat('id'), generateNextId(newValue));
    }
  });
  return newValue;
}

export function nodeHasBranches(node) {
  return ['condition', 'approval'].includes(node.nodeTypeToken);
}

export function lastShowBlankNode(nodeList) {
  return !nodeList.length || nodeHasBranches(nodeList[nodeList.length - 1]);
}

export function showBlankNode(nodeList, index) {
  if (!nodeList?.length) {
    return true;
  }
  const node = nodeList[index];
  const prevNode = nodeList[index - 1];
  return node?.nodeTypeToken === 'condition' && (!prevNode || nodeHasBranches(prevNode));
}

function getNodeDisplayId(currentKeyPath, nodeList, index) {
  const node = nodeList[index];

  if (node.nodeTypeToken === 'condition') {
    if (showBlankNode(nodeList, index)) {
      // condition node 本身不显示，如果上一个节点同时也是分支节点，则需要一个点来标注连线位置
      return `${currentKeyPath.concat('blank')}`;
    }
    return null;
  }

  return node.id;
}

export function extractEdges(value) {
  const edges = [];
  const t = getTFunc(['icp-form-renderer']);

  const dfsToBranches = (branches, keyPath, prev, next, branchType) => {
    if (!(Array.isArray(branches) && branches.length)) {
      return;
    }

    branches.forEach((branch, index) => {
      const { id, subFlow, result } = branch;
      const currentKeyPath = keyPath.concat(index);
      const lastHasBlankNode = lastShowBlankNode(subFlow);
      const subNext = lastHasBlankNode
        ? `${currentKeyPath.concat('subFlow,blank')}`
        : subFlow[subFlow.length - 1].id;

      let target;
      if (branchType === 'approval') {
        if (subFlow?.length) {
          target = getNodeDisplayId(currentKeyPath.concat('subFlow', 0), subFlow, 0);
        } else {
          target = subNext;
        }
      } else {
        target = id;
      }
      edges.push({
        source: prev,
        target,
        label: branchType === 'approval' ? t(`workflow.approval.result.${result}`) : undefined,
      });

      if (branchType === 'condition' && !subFlow?.length) {
        edges.push({
          source: id,
          target: subNext,
        });
      }

      if (subFlow?.length && lastHasBlankNode && !nodeHasBranches(subFlow[subFlow.length - 1])) {
        edges.push({
          source: subFlow[subFlow.length - 1].id,
          target: subNext,
        });
      }

      edges.push({
        source: subNext,
        target: next,
      });

      const subKeyPath = currentKeyPath.concat('subFlow');
      dfs(subFlow, subKeyPath, branchType === 'approval' ? null : id, subNext, false);
    });
  };

  const dfs = (nodeList, keyPath, prev, next) => {
    if (!(Array.isArray(nodeList) && nodeList.length)) {
      return;
    }

    nodeList.forEach((node, index) => {
      const currentKeyPath = keyPath.concat(index);
      let subNext;

      const nextNode = nodeList[index + 1];
      if (!nextNode) {
        subNext = next;
      } else if (nextNode.nodeTypeToken === 'condition') {
        subNext = `${keyPath.concat(index + 1)},blank`;
      } else {
        subNext = nextNode.id;
      }

      if (prev !== null && !(prev === 0 && node.nodeTypeToken === 'condition')) {
        const target = getNodeDisplayId(currentKeyPath, nodeList, index);
        if (target !== null) {
          edges.push({
            source: prev,
            target,
          });
        }
      }

      if (nodeHasBranches(node)) {
        let subPrev = '';
        if (node.nodeTypeToken === 'condition') {
          if (prev !== 0 && showBlankNode(nodeList, index)) {
            // condition node 本身不显示，如果上一个节点同时也是分支节点，则需要一个点来标注连线位置
            subPrev = `${currentKeyPath.concat('blank')}`;
          } else {
            subPrev = prev;
          }
        } else {
          subPrev = node.id;
        }
        const subKeyPath = currentKeyPath.concat('branches');
        dfsToBranches(node.branches, subKeyPath, subPrev, subNext, node.nodeTypeToken);
        prev = null;
        return;
      }

      prev = node.id;
      dfs(node.subFlow, currentKeyPath.concat('subFlow'), node.id, subNext, false);
    });
  };

  // 0 is start node
  // -1 is end node
  dfs(value.nodeList, ['nodeList'], 0, -1);

  if (value.nodeList?.length) {
    if (!nodeHasBranches(value.nodeList[value.nodeList.length - 1])) {
      edges.push({
        source: value.nodeList[value.nodeList.length - 1].id,
        target: -1,
      });
    }
  } else {
    // 空白流程
    edges.push({
      source: 0,
      target: -1,
    });
  }

  return edges;
}

const getDirection = (a, b) => {
  const getD = (x, y) => {
    if (Math.abs(x - y) < 1) return 0;
    return x > y ? -1 : 1;
  };
  return {
    x: getD(a.x, b.x),
    y: getD(a.y, b.y),
  };
};

const distance = (a, b) => Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

function getBend(a, b, c, size) {
  const bendSize = Math.min(distance(a, b) / 2, distance(b, c) / 2, size);
  const { x, y } = b;

  // no bend
  if ((a.x === x && x === c.x) || (a.y === y && y === c.y)) {
    return `L ${x} ${y}`;
  }

  const dir = getDirection(a, c);

  // first segment is horizontal
  if (a.y === b.y) {
    return `L ${x - bendSize * dir.x} ${y} Q ${x} ${y} ${x} ${y + bendSize * dir.y}`;
  }

  return `L ${x} ${y - bendSize * dir.y} Q ${x} ${y} ${x + bendSize * dir.x} ${y}`;
}

export function getEdgePath({ sourceX, sourceY, targetX, targetY, borderRadius = 24 }) {
  const direction = sourceY > targetY ? -1 : 1;
  const bendY = targetY - direction * (spacerSize - handleSize / 2);
  const points = [
    { x: sourceX, y: sourceY },
    { x: sourceX, y: bendY },
    { x: targetX, y: bendY },
    { x: targetX, y: targetY },
  ];

  return points
    .map((p, i) => {
      if (i === 0) {
        return `M ${p.x} ${p.y}`;
      }
      if (i === points.length - 1) {
        return `L ${p.x} ${p.y}`;
      }
      return getBend(points[i - 1], p, points[i + 1], borderRadius);
    })
    .join(' ');
}
